import React, { useCallback, useEffect, useMemo, useState } from "react";

import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import BinIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InsertPageBreakIcon from "@mui/icons-material/InsertPageBreak";
import { IconButton, Menu, MenuItem, Stack, TableCell, TableCellProps, TableRow, Typography } from "@mui/material";
import { ExtratableRepoIcon } from "assets/material-custom-icons";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { RoleComponentGuard } from "components/guards";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { ReportButton } from "components/reports";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { usePrompt } from "hooks";
import { usePickUserPermissions, useUserRoleSelector } from "store";
import { GetSplitDetailsReq, IDeal, MeasureDataTableRow, MonitoringDataFiles, Report } from "store/types";

import { FileType, RoleName } from "../../../../constants";

const batchIdKey = "batch_id";
// const enteredByKey = "entered_by";
const updatedByKey = "updated_by";

interface Props {
  row: MeasureDataTableRow;
  dealId?: IDeal["id"];
  inspectRow: (id: number) => void;
  deleteRow: (id: number, isGroup?: boolean) => void;
  hideChildCell?: boolean;
  borderLess?: boolean;
  columns: (keyof MeasureDataTableRow)[];
  inspectGroup: (data: GetSplitDetailsReq) => Promise<void>;
  isOpen: boolean;
  onFileClick: (file: { file_url: string; type: FileType; name: string }) => void;
  getReportsByGroup: (groupName: string) => Report[];
}
export const Row: React.FC<Props> = ({ row, onFileClick, ...restProps }) => {
  const { deleted_at, children, id } = row;
  const {
    // setMonitoringSaveOne,
    inspectRow,
    deleteRow,
    borderLess = false,
    columns = [],
    inspectGroup,
    isOpen
  } = restProps;
  const role = useUserRoleSelector();
  const [open, setOpen] = useState<boolean>(isOpen);
  const [filesAnchorEl, setFilesAnchorEl] = useState<null | HTMLElement>(null);
  const openPrompt = usePrompt();
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const isViewerPermission = dealMetrics && dealMetrics === "viewer";

  const userHasEditPermission = useMemo(() => {
    return role?.name === RoleName.Admin || role?.name === RoleName.Analyst;
  }, [role]);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const handleDelete = useCallback(() => {
    if (children?.length) {
      return deleteRow(children[0].id, true);
    }
    deleteRow(id);
  }, [children, deleteRow, id]);

  const border = useMemo(() => {
    const style = "0.5px solid #dae0e2";
    if (children?.length && open) return "none";
    if (borderLess) return "none";
    return style;
  }, [children, open, borderLess]);

  const commonCellStyles: TableCellProps["sx"] = {
    // display: "flex",
    borderBottom: border,
    fontSize: "15px",
    color: deleted_at ? "#0a314280" : "#013951",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "250px",
    backgroundColor: "transparent",
    textDecoration: deleted_at ? "line-through" : "none",
    height: "50px"
  };

  return (
    <>
      <TableRow
        sx={{
          transition: "background-color 0.2s linear",
          ".hiddable": {
            transition: "all 0.2s linear",
            opacity: 0
          },
          ":hover": {
            ".hiddable": {
              opacity: 1
            }
          }
        }}
      >
        <TableCell sx={commonCellStyles}>
          {!!children?.length && (
            <IconButton
              sx={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
              onClick={() => setOpen(!open)}
              size="sm"
            >
              <ArrowForwardIosOutlinedIcon sx={{ fontSize: "16px", color: "#0a3142" }} />
            </IconButton>
          )}
        </TableCell>
        {columns.map((key, index) => (
          <TableCell
            key={key}
            align={!index ? "left" : "center"}
            sx={{ ...commonCellStyles, cursor: key === batchIdKey ? "pointer" : "default" }}
          >
            <MaterialTooltip
              onClick={() => {
                if (key !== batchIdKey) return;
                if (!row.batch_id || !userHasEditPermission) return;
                inspectGroup({ deal_id: row.parent_id || row.deal_id, batch_id: row.batch_id });
              }}
              // isShow
              placement="top"
              title={row[key] as string}
            >
              <Stack>
                {key === "deal" && !deleted_at ? (
                  <MaterialTooltip title={row[key] as string} isShow>
                    <span
                      style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
                    >
                      <NavigateLink
                        allowNewTab={true}
                        route="dealDashboard"
                        params={{ deal_id: String(row.deal_id) }}
                        style={{ color: "#26a6ae" }}
                      >
                        <>{row[key] as string}</>
                      </NavigateLink>
                    </span>
                  </MaterialTooltip>
                ) : (
                  <MaterialTooltip title={row[key] as string}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "15px",
                        color: deleted_at ? "#0a314280" : "#013951",
                        display: "block"
                      }}
                    >
                      {row[key] as string}
                    </Typography>
                  </MaterialTooltip>
                )}
                {key === updatedByKey && row.updated_at && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden"
                    }}
                  >
                    On: {row.updated_at}
                  </Typography>
                )}
              </Stack>
            </MaterialTooltip>
          </TableCell>
        ))}

        <TableCell sx={commonCellStyles}>
          {!deleted_at && (
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermission}>
              <Stack direction="row">
                <ReportButton
                  group="measure_data"
                  context={{
                    measure_data_id: row.id,
                    batch_id: row.batch_id,
                    deal_id: row.deal_id,
                    parent_id: row.parent_id,
                    is_child: restProps.hideChildCell === true,
                    children_count: row.children?.length || 0
                  }}
                  icon={<InsertPageBreakIcon sx={{ fontSize: "16px" }} />}
                  buttonProps={{ size: "small", className: "hiddable" }}
                  getReportsByGroup={restProps.getReportsByGroup}
                />
                <IconButton className="hiddable" size="sm" onClick={handleDelete}>
                  <BinIcon />
                </IconButton>
                <IconButton
                  className="hiddable"
                  onClick={() =>
                    children?.length && row.split_id
                      ? inspectGroup({ deal_id: row.deal_id, split_id: row.split_id })
                      : inspectRow(id)
                  }
                  size="sm"
                >
                  <CreateOutlinedIcon />
                </IconButton>

                {Boolean(row.files?.length) && (
                  <>
                    <Stack className="hiddable" direction="row" gap="5px" alignItems="center">
                      <IconButton onClick={(event) => setFilesAnchorEl(event.currentTarget)} size="sm">
                        <DescriptionOutlinedIcon sx={{ color: "#26A6AE" }} />
                      </IconButton>

                      <Typography color="#26A6AE">+{row.files?.length}</Typography>
                    </Stack>
                    <Menu
                      sx={{ p: "10px" }}
                      anchorEl={filesAnchorEl}
                      open={Boolean(filesAnchorEl)}
                      onClose={() => setFilesAnchorEl(null)}
                    >
                      {row.files?.map((file: MonitoringDataFiles) => (
                        <MenuItem
                          key={file.id}
                          onClick={() => onFileClick({ file_url: file.file_url, type: FileType.PDF, name: file.name })}
                        >
                          <Stack direction="row" gap="10px">
                            <DescriptionOutlinedIcon />
                            <MaterialTooltip isShow title={file.name}>
                              <Typography
                                sx={{
                                  maxWidth: "150px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis"
                                }}
                              >
                                {file.name}
                              </Typography>
                            </MaterialTooltip>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </Stack>
            </RoleComponentGuard>
          )}
        </TableCell>
        <TableCell sx={commonCellStyles} align="center">
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermission}>
            {!!row.tags?.length && (
              <MaterialTooltip
                isShow
                title={
                  <>
                    Open <strong>{row.tags.map((tag) => tag.file_name).join(", ")}</strong> in Data Extractor
                  </>
                }
                placement="top"
              >
                <IconButton
                  onClick={async () => {
                    if (!row.tags) return;
                    const fileIds = Array.from(new Set(row.tags.map((tag) => tag.file_id)));
                    const fileNames = row.tags.reduce<Record<number, string>>((acc, tag) => {
                      acc[tag.file_id] = tag.file_name;
                      return acc;
                    }, {});
                    await openPrompt({
                      componentType: "fullscreen",
                      Component: DataExtractionModal,
                      props: { fileIds, fileNames }
                    });
                  }}
                >
                  <ExtratableRepoIcon sx={{ width: 20, height: 16, fill: "#0a3142" }} />
                </IconButton>
              </MaterialTooltip>
            )}
          </RoleComponentGuard>
        </TableCell>
      </TableRow>
      {open &&
        row?.children?.map((row, index, arr) => (
          <Row
            key={id || index}
            {...restProps}
            row={row}
            hideChildCell
            borderLess={index < arr.length - 1}
            onFileClick={onFileClick}
          />
        ))}
    </>
  );
};
